import React, { useState, useCallback, useRef } from "react";
import { Buttons, HeaderButton } from "components";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import s from "styles/pages/Pages.module.scss";
import { Cached, InfoOutlined, FlightRounded, AirplanemodeActiveRounded, AirlineSeatReclineExtraRounded } from "@mui/icons-material";
import Filters from "components/Filters";
import { useFormik } from "formik";
import XGridServer from "components/UI/Grid/XGridServer";
import Modal from "components/UI/Modal/Modal";
import { Toaster } from "sonner";

import { migraUsuarioPasoGet } from "services/services-mia/migra";
import { useMonitorPasosSSE } from "./hooks/useMonitorPasosSSE";
import { getStreamHeaders, MIGRA_SSE_STREAM_URL, migraUsuarioPasoDetalleGet } from "./services/registros";
import { monitorColumns } from "./utils/monitorColumns";
import DetalleModal from "./components/DetalleModal";
import { VueloModal, VueloDetalleModal } from "pages/migra/admin/Actions";
import { mapMigraUsuarioPasoRow } from "pages/migra/utils/usuarioPaso";
import { getFechaHoy } from "utils/date";

const TIPO_FILTRO_OPTIONS = [
    { cod: "", nombre: "Todos" },
    { cod: "E", nombre: "Entrada" },
    { cod: "S", nombre: "Salida" },
];

const ESTADO_FILTRO_OPTIONS = [
    { cod: "", nombre: "Todos" },
    { cod: "P", nombre: "Pendientes" },
    { cod: "C", nombre: "Completados" },
    { cod: "B", nombre: "Bloqueados" },
];

const valoresIniciales = (pasoDefault) => ({
    paso: pasoDefault,
    usuarioApellidos: "",
    usuarioNroDeDocumentoX: "",
    tipo: "",
    estado: "",
    fecha: getFechaHoy(),
});

export function MonitorPasosContent({
    pasoDefault,
    pasos,
    credentials,
    errorPasos,
    loadingPasos,
}) {
    const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
    const [forceUpload, setForceUpload] = useState(false);
    const [resetTable, setResetTable] = useState(false);
    const [filtrosAplicados, setFiltrosAplicados] = useState(() => valoresIniciales(pasoDefault));
    const [modalDetalle, setModalDetalle] = useState({ isOpen: false });
    const [modalVuelo, setModalVuelo] = useState({ isOpen: false });
    const [modalVueloDetalle, setModalVueloDetalle] = useState({ isOpen: false });

    const skipResetRef = useRef(false);
    const currentPageRef = useRef(0);

    const getStreamBody = useCallback(() => ({
        pasos: [filtrosAplicados.paso],
    }), [filtrosAplicados.paso]);

    const getStreamHeadersFn = useCallback(() =>
        getStreamHeaders(credentials),
        [credentials]
    );

    const fetchDetalle = useCallback((id) =>
        migraUsuarioPasoDetalleGet({ id, credentials }),
        [credentials]
    );

    const {
        isConnected,
        reconnectAttempt,
        hasGivenUp,
        reconnect,
        nuevosEventos,
        resetNuevosEventos,
    } = useMonitorPasosSSE({
        url: MIGRA_SSE_STREAM_URL,
        getBody: getStreamBody,
        getHeaders: getStreamHeadersFn,
        getDetalle: fetchDetalle,
        subscriptionKey: filtrosAplicados.paso,
        credentials,
        onRefresh: () => {
            skipResetRef.current = true;
            setForceUpload(v => !v);
        },
        shouldRefresh: () => currentPageRef.current === 0 && filtrosAplicados.fecha === getFechaHoy(),
        throttleMs: 15000,
    });

    const updateList = useCallback(async (page, pageSize) => {
        currentPageRef.current = page;

        if (page === 0) resetNuevosEventos();
        if (!skipResetRef.current) setRowSelected({ status: false, row: {} });
        skipResetRef.current = false;

        const { result, total } = await migraUsuarioPasoGet(
            {
                migraPasoId: filtrosAplicados.paso || null,
                usuarioApellidos: filtrosAplicados.usuarioApellidos || null,
                usuarioNroDeDocumentoX: filtrosAplicados.usuarioNroDeDocumentoX || null,
                incluirVencidos: "Y",
                migraUsuarioPasoTipo: filtrosAplicados.tipo || null,
                migraUsuarioPasoEstado: filtrosAplicados.estado || null,
                fechaDesde: filtrosAplicados.fecha || null,
                fechaHasta: filtrosAplicados.fecha || null,
                page: page + 1,
                pageSize,
            },
            credentials
        );

        const rows = (result || []).map(mapMigraUsuarioPasoRow);
        return { rows, rowCount: total };
    }, [resetNuevosEventos, filtrosAplicados, credentials]);

    const formik = useFormik({
        initialValues: valoresIniciales(pasoDefault),
        onSubmit: (values) => {
            if (!values.paso) return;
            setFiltrosAplicados(values);
            setResetTable(v => !v);
        },
    });

    const handleLimpiarFiltros = () => {
        const iniciales = valoresIniciales(pasoDefault);
        formik.resetForm({ values: iniciales });
        setFiltrosAplicados(iniciales);
        setResetTable(v => !v);
    };

    return (
        <main className={s.main}>
            <Toaster position="top-right" expand={true} />
            <Modal
                open={modalDetalle.isOpen}
                title="Detalle del registro"
                onClose={() => setModalDetalle({ isOpen: false })}
                wide
            >
                {modalDetalle.isOpen && <DetalleModal row={rowSelected.row} />}
            </Modal>
            <Modal
                open={modalVuelo.isOpen}
                title="Asignar Vuelo"
                onClose={() => setModalVuelo({ isOpen: false })}
            >
                {modalVuelo.isOpen && (
                    <VueloModal
                        row={rowSelected.row}
                        updateList={() => {
                            skipResetRef.current = true;
                            setForceUpload((v) => !v);
                        }}
                        closeModal={() => setModalVuelo({ isOpen: false })}
                        credentials={credentials}
                        setRowSelected={setRowSelected}
                    />
                )}
            </Modal>
            <Modal
                open={modalVueloDetalle.isOpen}
                title="Detalle del Vuelo"
                onClose={() => setModalVueloDetalle({ isOpen: false })}
                wide
            >
                {modalVueloDetalle.isOpen && (
                    <VueloDetalleModal
                        row={rowSelected.row}
                        updateList={() => {
                            skipResetRef.current = true;
                            setForceUpload((v) => !v);
                        }}
                        closeModal={() => setModalVueloDetalle({ isOpen: false })}
                        credentials={credentials}
                        setRowSelected={setRowSelected}
                    />
                )}
            </Modal>
            <div className={s["filters-actions"]}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Filters
                        formik={formik}
                        inputsRender={[
                            {
                                type: "select",
                                name: "paso",
                                label: loadingPasos ? "Cargando pasos..." : "Paso",
                                width: "220px",
                                options: pasos,
                                firstOptionKey: "migraPasoId",
                                firstOptionValue: "migraPasoId",
                                firstOptionName: "migraPasoNombre",
                                disabled: loadingPasos || !!errorPasos,
                            },
                            { type: "text", name: "usuarioApellidos", label: "Apellidos" },
                            { type: "text", name: "usuarioNroDeDocumentoX", label: "Documento" },
                            {
                                type: "select",
                                name: "tipo",
                                label: "Tipo",
                                options: TIPO_FILTRO_OPTIONS,
                                firstOptionKey: "cod",
                                firstOptionName: "nombre",
                                firstOptionValue: "cod",
                            },
                            {
                                type: "select",
                                name: "estado",
                                label: "Estado",
                                options: ESTADO_FILTRO_OPTIONS,
                                firstOptionKey: "cod",
                                firstOptionName: "nombre",
                                firstOptionValue: "cod",
                            },
                            { type: "date", name: "fecha", label: "Fecha" },
                        ]}
                    />
                    <HeaderButton text="Limpiar filtros" onClick={handleLimpiarFiltros}>
                        <FilterAltOffRoundedIcon />
                    </HeaderButton>
                    {errorPasos && (
                        <span style={{ color: "#d32f2f", fontSize: "0.8em", fontWeight: "500" }}>
                            {errorPasos}
                        </span>
                    )}
                    {!formik.values.paso && (
                        <span style={{ color: "#d32f2f", fontSize: "0.75em", marginTop: 4 }}>
                            Seleccioná un paso para filtrar
                        </span>
                    )}
                </div>

                {nuevosEventos > 0 && (
                    <button
                        type="button"
                        onClick={() => {
                            const hoy = getFechaHoy();
                            resetNuevosEventos();
                            formik.setFieldValue("fecha", hoy);
                            setFiltrosAplicados(prev => ({ ...prev, fecha: hoy }));
                            setResetTable(v => !v);
                        }}
                        style={{
                            marginRight: "1rem",
                            padding: "0.4rem 0.8rem",
                            background: "#29b6f6",
                            color: "white",
                            border: "none",
                            borderRadius: 4,
                            cursor: "pointer",
                            fontSize: "0.85em",
                            fontWeight: "bold",
                        }}
                    >
                        🔵 {nuevosEventos} {nuevosEventos === 1 ? "nuevo evento" : "nuevos eventos"}
                    </button>
                )}

                {hasGivenUp ? (
                    <button
                        type="button"
                        onClick={reconnect}
                        style={{
                            marginRight: "1rem",
                            padding: "0.4rem 0.8rem",
                            background: "#f44336",
                            color: "white",
                            border: "none",
                            borderRadius: 4,
                            cursor: "pointer",
                            fontSize: "0.85em",
                        }}
                    >
                        🔄 Reintentar conexión
                    </button>
                ) : (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "4px 10px",
                        borderRadius: "16px",
                        fontSize: "0.82em",
                        fontWeight: 500,
                        color: "#374151",
                    }}>
                        <span
                            className={isConnected ? s.pulso : undefined}
                            style={{
                                display: "inline-block",
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: isConnected ? "#4caf50" : reconnectAttempt > 0 ? "#ff9800" : "#f44336",
                            }}
                        />
                        {isConnected
                            ? "En vivo"
                            : reconnectAttempt > 0
                                ? `Reconectando (${reconnectAttempt}/10)`
                                : "Desconectado"}
                    </div>
                )}

                <Buttons>
                    <HeaderButton
                        type="button"
                        text="Refrescar"
                        onClick={() => {
                            skipResetRef.current = true;
                            setForceUpload(v => !v);
                        }}
                    >
                        <Cached />
                    </HeaderButton>
                    <HeaderButton
                        type="button"
                        text="Ver detalle"
                        onClick={() => setModalDetalle({ isOpen: true })}
                        disabled={!rowSelected.row.id}
                    >
                        <InfoOutlined />
                    </HeaderButton>
                    <HeaderButton
                        type="button"
                        text="Asignar vuelo"
                        onClick={() => setModalVuelo({ isOpen: true })}
                        disabled={!rowSelected.row.id || rowSelected.row.migraUsuarioPasoEstado !== "P"}
                    >
                        <AirlineSeatReclineExtraRounded />
                    </HeaderButton>
                    {/* <HeaderButton
                        type="button"
                        text="Ver vuelo"
                        onClick={() => setModalVueloDetalle({ isOpen: true })}
                        disabled={!rowSelected.row.migraVueloId}
                    >
                        <AirplanemodeActiveRounded />
                    </HeaderButton> */}
                </Buttons>
            </div>

            <div className={s.container}>
                <XGridServer
                    titles={monitorColumns}
                    updateList={updateList}
                    forceReset={resetTable}
                    forceUpload={forceUpload}
                    secondarySelected={rowSelected}
                    setSecondarySelected={setRowSelected}
                    autoRow={"auto"}
                />
            </div>
        </main>
    );
}
